<template>
  <main>
    <!-- ── About ───────────────────────────────────────────── -->
    <section id="about" class="lp-v2-about">
      <div class="lp-v2-container">
        <div class="lp-v2-about-grid">
          <!-- Left: text -->
          <div class="lp-v2-about-text">
            <span class="lp-v2-label">About Us</span>
            <h2 class="lp-v2-about-title">{{ landingPageData.title || 'About Our Company' }}</h2>
            <div class="lp-v2-about-desc" v-html="bodyDescription" />
            <!-- Social Media -->
            <div v-if="hasSocial" class="lp-v2-social-wrap">
              <span class="lp-v2-social-label">Follow Us On</span>
              <div class="lp-v2-social-links">
                <a v-if="landingPageData.instagram" :href="landingPageData.instagram" target="_blank" aria-label="Instagram"><i class="bi bi-instagram" /></a>
                <a v-if="landingPageData.facebook" :href="landingPageData.facebook" target="_blank" aria-label="Facebook"><i class="bi bi-facebook" /></a>
                <a v-if="landingPageData.linkedin" :href="landingPageData.linkedin" target="_blank" aria-label="LinkedIn"><i class="bi bi-linkedin" /></a>
                <a v-if="landingPageData.youtube" :href="landingPageData.youtube" target="_blank" aria-label="YouTube"><i class="bi bi-youtube" /></a>
                <a v-if="landingPageData.tiktok" :href="landingPageData.tiktok" target="_blank" aria-label="TikTok"><i class="bi bi-tiktok" /></a>
              </div>
            </div>
          </div>
          <!-- Right: decorative image/placeholder -->
          <div class="lp-v2-about-visual">
            <div class="lp-v2-about-img-wrap">
              <img
                v-if="landingPageData.picture_hero_link"
                :src="landingPageData.picture_hero_link"
                :alt="landingPageData.title"
                class="lp-v2-about-img"
              />
              <div v-else class="lp-v2-about-img-placeholder" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Value / Stats ──────────────────────────────────── -->
    <section class="lp-v2-value">
      <div class="lp-v2-container">
        <h3 class="lp-v2-value-title">
          More Than 8 Years of Delivering Nationwide Telecommunications Projects
        </h3>
        <div class="lp-v2-value-list">
          <div v-for="stat in stats" :key="stat.number" class="lp-v2-value-item">
            <span class="lp-v2-value-number">+{{ stat.number }}</span>
            <span class="lp-v2-value-desc">{{ stat.desc }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Services ───────────────────────────────────────── -->
    <section id="services" class="lp-v2-services">
      <div class="lp-v2-container">
        <div class="lp-v2-services-head">
          <span class="lp-v2-label">Our Services</span>
          <h2 class="lp-v2-section-title">What We Offer</h2>
          <p class="lp-v2-section-desc">
            We provide end-to-end telecommunications network construction services, material supply,
            and skilled manpower to support infrastructure development nationwide.
          </p>
        </div>
        <div class="lp-v2-services-list">
          <div
            v-for="(service, i) in services"
            :key="service.title"
            class="lp-v2-service-card"
            :style="{ backgroundImage: service.bg ? `url(${service.bg})` : 'none' }"
          >
            <div class="lp-v2-service-overlay" />
            <div class="lp-v2-service-body">
              <span class="lp-v2-service-num">0{{ i + 1 }}</span>
              <h4 class="lp-v2-service-title">{{ service.title }}</h4>
              <p class="lp-v2-service-desc">{{ service.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Our Partners ───────────────────────────────────── -->
    <section id="our-clients" class="lp-v2-clients">
      <div class="lp-v2-container">
        <span class="lp-v2-label text-center d-block">Partners</span>
        <h2 class="lp-v2-section-title text-center">Our Partner</h2>
        <p class="lp-v2-section-desc text-center mx-auto">
          Trusted by leading companies across Indonesia's telecommunications and infrastructure sectors.
        </p>
        <div class="lp-v2-clients-track-wrap">
          <div class="lp-v2-clients-track">
            <div
              v-for="(logo, i) in [...partnerLogos, ...partnerLogos]"
              :key="i"
              class="lp-v2-client-item"
            >
              <img :src="logo.src" :alt="logo.name" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Map / Location ─────────────────────────────────── -->
    <section id="map-home" class="lp-v2-map">
      <div class="lp-v2-map-blob">
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,200 C150,350 400,50 600,200 C800,350 900,150 900,300 L900,600 L0,600 Z" fill="rgba(22,117,188,0.06)" />
        </svg>
      </div>
      <div class="lp-v2-container">
        <span class="lp-v2-label text-center d-block">Where We Are</span>
        <h2 class="lp-v2-section-title text-center">Our Location</h2>
        <p class="lp-v2-section-desc text-center mx-auto">
          Operating across almost the entire Indonesian archipelago, from Sumatra to Papua.
        </p>
        <div class="lp-v2-map-img-wrap">
          <img
            v-if="landingPageData.map_image"
            :src="landingPageData.map_image"
            alt="Our Location"
            class="lp-v2-map-img"
          />
          <div v-else class="lp-v2-map-placeholder">
            <i class="bi bi-geo-alt" />
            <p>Map image will appear here</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Testimonials ───────────────────────────────────── -->
    <section id="testi" class="lp-v2-testi">
      <div class="lp-v2-container">
        <span class="lp-v2-label">Reviews</span>
        <h2 class="lp-v2-testi-title">Our Customer Review</h2>
        <p class="lp-v2-testi-desc">Hear what our clients say about working with us.</p>

        <b-carousel
          id="lp-v2-testi-carousel"
          :interval="4000"
          indicators
          class="lp-v2-testi-carousel"
        >
          <b-carousel-slide v-for="(group, gi) in testimonialGroups" :key="gi">
            <template #img><div style="display:none" /></template>
            <div class="lp-v2-testi-group">
              <div v-for="testi in group" :key="testi.name" class="lp-v2-testi-card">
                <p class="lp-v2-testi-text">"{{ testi.text }}"</p>
                <div class="lp-v2-testi-author">
                  <strong>{{ testi.name }}</strong>
                  <span>{{ testi.role }}</span>
                </div>
              </div>
            </div>
          </b-carousel-slide>
        </b-carousel>
      </div>
    </section>
  </main>
</template>

<script>
import { BCarousel, BCarouselSlide } from 'bootstrap-vue'

export default {
  components: { BCarousel, BCarouselSlide },
  props: {
    bodyDescription: { type: String, default: '' },
    landingPageData: { type: Object, required: true },
    socialMedia: { type: [Boolean, String, Number], default: false },
  },
  data() {
    return {
      stats: [
        { number: '350K', desc: 'Home Connect' },
        { number: '260K', desc: 'Fiber to the Home' },
        { number: '12M', desc: 'Outside Plant Fiber Optic' },
        { number: '500', desc: 'Telecommunication Installation' },
      ],
      services: [
        {
          bg: null,
          title: 'Construction Service',
          desc: 'End-to-end fiber optic network construction services delivered with precision and reliability across the archipelago.',
        },
        {
          bg: null,
          title: 'Material Supply',
          desc: 'High-quality fiber optic materials and components sourced and supplied to project sites nationwide.',
        },
        {
          bg: null,
          title: 'Manpower Services',
          desc: 'Skilled and certified technicians available for deployment across all regions of Indonesia.',
        },
      ],
      partnerLogos: [
        { src: 'https://via.placeholder.com/160x60?text=Partner+1', name: 'Partner 1' },
        { src: 'https://via.placeholder.com/160x60?text=Partner+2', name: 'Partner 2' },
        { src: 'https://via.placeholder.com/160x60?text=Partner+3', name: 'Partner 3' },
        { src: 'https://via.placeholder.com/160x60?text=Partner+4', name: 'Partner 4' },
        { src: 'https://via.placeholder.com/160x60?text=Partner+5', name: 'Partner 5' },
        { src: 'https://via.placeholder.com/160x60?text=Partner+6', name: 'Partner 6' },
      ],
      testimonials: [
        { name: 'KUSUMA', role: 'Head Of Marketing', text: 'Atlantic Construction delivered our project on time with exceptional quality. Their team was professional and responsive throughout.' },
        { name: 'MIKE', role: 'Project Manager', text: 'Outstanding service and reliable execution. We have worked with them on multiple projects and they never disappoint.' },
        { name: 'SASHA', role: 'Marketing Director', text: 'Their fiber optic installation was seamless. The crew was efficient and left the site clean. Highly recommended.' },
        { name: 'HANDY', role: 'Marketing Manager', text: 'Great communication and transparency throughout the project. We always knew the status and timeline of our work.' },
        { name: 'BUDI', role: 'Site Supervisor', text: 'The technical expertise of their team is impressive. Every challenge was handled professionally and efficiently.' },
        { name: 'RINA', role: 'Operations Head', text: 'Reliable partner for our nationwide rollout. Their logistic support and manpower quality exceeded our expectations.' },
      ],
    }
  },
  computed: {
    hasSocial() {
      const d = this.landingPageData
      return d.instagram || d.facebook || d.linkedin || d.youtube || d.tiktok
    },
    perGroup() {
      if (typeof window === 'undefined') return 3
      if (window.innerWidth >= 992) return 3
      if (window.innerWidth >= 576) return 2
      return 1
    },
    testimonialGroups() {
      const groups = []
      for (let i = 0; i < this.testimonials.length; i += this.perGroup) {
        groups.push(this.testimonials.slice(i, i + this.perGroup))
      }
      return groups
    },
  },
}
</script>

<style lang="scss" scoped>
// ── Design Tokens
$primary: #1675bc;
$primary-dark: #0d5fa0;
$value-gradient: linear-gradient(270deg, #3963B8 44.58%, #03B2BD 100%);
$testi-card-bg: #1C61B5;
$dark: #0d1b2e;

.lp-v2-container {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.lp-v2-label {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 0.6rem;
}

.lp-v2-section-title {
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.25;
  margin-bottom: 1rem;
}

.lp-v2-section-desc {
  color: #555;
  font-size: 1rem;
  line-height: 1.75;
  max-width: 640px;
  margin-bottom: 2rem;
}

/* ── About ─────────────────────────────────────────────── */
.lp-v2-about {
  padding: 5rem 0;
  background: #fff;
}

.lp-v2-about-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
}

.lp-v2-about-title {
  font-size: clamp(1.5rem, 3vw, 2.4rem);
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.25;
  margin-bottom: 1.25rem;
}

.lp-v2-about-desc {
  color: #444;
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;

  :deep(ul) { padding-left: 1.25rem; margin-bottom: 0.75rem; }
  :deep(p)  { margin-bottom: 0.75rem; }
  :deep(a)  { color: $primary; }
  :deep(strong) { color: #1a1a2e; }
}

.lp-v2-social-wrap {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.lp-v2-social-label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #333;
}

.lp-v2-social-links {
  display: flex;
  gap: 0.6rem;

  a {
    font-size: 1.2rem;
    color: #555;
    transition: color 0.2s;
    &:hover { color: $primary; }
  }
}

.lp-v2-about-visual {
  @media (max-width: 991px) { display: none; }
}

.lp-v2-about-img-wrap {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0,0,0,.14);
}

.lp-v2-about-img {
  width: 100%;
  height: 420px;
  object-fit: cover;
  display: block;
}

.lp-v2-about-img-placeholder {
  width: 100%;
  height: 420px;
  background: linear-gradient(135deg, #e8f4fd 0%, #d0e8f8 100%);
  border-radius: 16px;
}

/* ── Value ──────────────────────────────────────────────── */
.lp-v2-value {
  background: $value-gradient;
  padding: 5rem 0;
}

.lp-v2-value-title {
  color: #fff;
  font-size: clamp(1rem, 2.2vw, 1.4rem);
  font-weight: 400;
  max-width: 680px;
  margin: 0 auto 4rem;
  text-align: center;
  line-height: 1.5;
  opacity: 0.95;
}

.lp-v2-value-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); gap: 1.5rem; }
}

.lp-v2-value-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.lp-v2-value-number {
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  color: #fff;
  line-height: 1;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;

  @media (max-width: 767px) { font-size: 2.2rem; }
}

.lp-v2-value-desc {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  line-height: 1.45;
  max-width: 140px;
}

/* ── Services ───────────────────────────────────────────── */
.lp-v2-services {
  padding: 5rem 0;
  background: #f7f9fc;
}

.lp-v2-services-head {
  max-width: 640px;
  margin-bottom: 3rem;
}

.lp-v2-services-list {
  display: flex;
  gap: 0;

  @media (max-width: 767px) {
    flex-direction: column;
  }
}

.lp-v2-service-card {
  position: relative;
  flex: 1;
  min-height: 40rem;
  background-color: #1a1a2e;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  transition: flex 0.3s ease;

  &:hover { flex: 1.3; }
  &:hover .lp-v2-service-overlay { opacity: 1; }

  @media (max-width: 767px) {
    min-height: 22rem;
    flex: none !important;
    &:hover { flex: none !important; }
  }
}

.lp-v2-service-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(22, 117, 188, 0.0) 0%, rgba(22, 117, 188, 0.72) 100%);
  transition: opacity 0.3s;
  opacity: 0.6;

  // Always show gradient at bottom
  background: linear-gradient(180deg, transparent 40%, rgba(7, 9, 63, 0.82) 100%);
}

.lp-v2-service-body {
  position: relative;
  z-index: 2;
  padding: 2.5rem 1.75rem;
}

.lp-v2-service-num {
  display: block;
  font-size: 3rem;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.2);
  line-height: 1;
  margin-bottom: 0.5rem;
}

.lp-v2-service-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.lp-v2-service-desc {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  line-height: 1.65;
  margin: 0;
}

/* ── Clients ────────────────────────────────────────────── */
.lp-v2-clients {
  padding: 5rem 0;
  background: #fff;
}

.lp-v2-clients-track-wrap {
  overflow: hidden;
  margin-top: 2.5rem;
}

.lp-v2-clients-track {
  display: flex;
  gap: 1.5rem;
  animation: lp-v2-scroll 22s linear infinite;

  &:hover { animation-play-state: paused; }
}

.lp-v2-client-item {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 80px;
  padding: 0.75rem 1.25rem;
  background: #f4f7fa;
  border-radius: 10px;
  border: 1px solid #e8edf2;

  img {
    max-width: 100%;
    max-height: 50px;
    object-fit: contain;
    filter: grayscale(1) opacity(0.65);
    transition: filter 0.25s, opacity 0.25s;
  }

  &:hover img {
    filter: none;
    opacity: 1;
  }
}

@keyframes lp-v2-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* ── Map ────────────────────────────────────────────────── */
.lp-v2-map {
  padding: 5rem 0;
  background: #f7f9fc;
  position: relative;
  overflow: hidden;
}

.lp-v2-map-blob {
  position: absolute;
  inset: 0;
  pointer-events: none;

  svg {
    width: 100%;
    height: 100%;
  }
}

.lp-v2-map-img-wrap {
  margin-top: 2rem;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,.1);
  position: relative;
  z-index: 1;
}

.lp-v2-map-img {
  width: 100%;
  max-height: 500px;
  object-fit: cover;
  display: block;
}

.lp-v2-map-placeholder {
  background: #e4ecf4;
  border-radius: 16px;
  height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  position: relative;
  z-index: 1;

  i { font-size: 3.5rem; color: #bbb; }
  p { margin-top: 0.75rem; font-size: 0.9rem; }
}

/* ── Testimonials ───────────────────────────────────────── */
.lp-v2-testi {
  padding: 5rem 0;
  background: $dark;
}

.lp-v2-testi-title {
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
  margin-bottom: 0.75rem;
}

.lp-v2-testi-desc {
  color: rgba(255, 255, 255, 0.65);
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 2.5rem;
}

.lp-v2-testi-carousel {
  :deep(.carousel-indicators) {
    bottom: -1.5rem;

    li {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(255,255,255,0.35);
      border: none;
      &.active { background: #fff; }
    }
  }
  :deep(.carousel-control-prev),
  :deep(.carousel-control-next) { display: none; }
  :deep(.carousel-item) { min-height: 1px; }
  :deep(.carousel-caption) {
    position: static;
    padding: 0;
    color: inherit;
    text-align: left;
  }
}

.lp-v2-testi-group {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  padding-bottom: 3.5rem;

  @media (max-width: 991px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 575px) { grid-template-columns: 1fr; }
}

.lp-v2-testi-card {
  background: $testi-card-bg;
  border-radius: 15px;
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 200px;

  @media (max-width: 767px) { padding: 1.5rem; border-radius: 8px; }
}

.lp-v2-testi-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.925rem;
  line-height: 1.75;
  margin-bottom: 1.5rem;
  flex: 1;
}

.lp-v2-testi-author {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  strong {
    color: #fff;
    font-size: 0.9rem;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  span {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.8rem;
  }
}
</style>
